import React from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';
import SettingsRowShell from './SettingsRowShell';
import { formatUtcToLocal } from '../../utils/dateUtils';

const JiraRow = ({
  jiraStatus,
  loadingJira,
  onRefresh,
  onConnect,
  onDisconnect,
}) => {
  const statusChip = loadingJira
    ? null
    : jiraStatus.connected
      ? {
          label: jiraStatus.expired
            ? 'Token Expired'
            : `Connected — ${jiraStatus.display_name || jiraStatus.email || 'JIRA User'}`,
          color: jiraStatus.expired ? 'warning' : 'success',
        }
      : { label: 'Not Connected', color: 'warning' };

  return (
    <SettingsRowShell
      icon={<BugReportIcon />}
      title="JIRA Connection"
      description="Transition tickets and add comments using your own permissions"
      status={statusChip}
      loading={loadingJira}
      actions={
        <IconButton onClick={onRefresh} disabled={loadingJira} size="small">
          <RefreshIcon fontSize="small" />
        </IconButton>
      }
    >
      {loadingJira ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box>
          {/* Status detail */}
          {jiraStatus.connected ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  {jiraStatus.display_name || jiraStatus.email || 'JIRA User'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {jiraStatus.email}
                  {jiraStatus.expired && (
                    <Chip label="Expired" size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                  )}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connect your JIRA account to allow QAssure AI to search issues, create bugs, and manage tickets on your behalf.
            </Typography>
          )}

          {/* Token expiry */}
          {jiraStatus.connected && jiraStatus.expires_at && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Token expires: {formatUtcToLocal(jiraStatus.expires_at)}
              </Typography>
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {jiraStatus.connected ? (
              <>
                <Button variant="outlined" size="small" onClick={onConnect} startIcon={<RefreshIcon />}>
                  Re-authenticate
                </Button>
                <Button variant="outlined" color="error" size="small" onClick={onDisconnect} startIcon={<LinkOffIcon />}>
                  Disconnect
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                size="small"
                onClick={onConnect}
                startIcon={<LinkIcon />}
                disabled={!jiraStatus.configured}
              >
                Connect JIRA
              </Button>
            )}
          </Box>

          {!jiraStatus.configured && (
            <Alert severity="warning" sx={{ mt: 2 }} icon={false}>
              JIRA OAuth is not configured on the server.
            </Alert>
          )}
        </Box>
      )}
    </SettingsRowShell>
  );
};

export default JiraRow;
